import React, { useState, useCallback, forwardRef, useImperativeHandle, useRef, useEffect } from 'react';
import styled from 'styled-components';
import VideoPlayer from './VideoPlayer';
import useInfiniteScroll from '../hooks/useInfiniteScroll';
import { fetchVideos } from '../api/videoService';
import { FaSpinner, FaSync, FaExclamationTriangle } from 'react-icons/fa';
import { usePoints } from '../hooks/PointsContext';
import { pruneResumeWindow } from '../hooks/useSessionResume';

// CSS snap scroll container — fills its containing block via absolute inset:0.
// position:absolute with explicit top/right/bottom/left gives the browser a concrete
// pixel height, which children can correctly use height:100% against. height:100%
// on a flex child is unreliable on iOS Chrome (flex-derived heights are not
// treated as "definite" for child percentage resolution).
const FeedContainer = styled.div.attrs({ className: 'feed-container' })`
  position: absolute;
  top: 60px;
  left: 0;
  right: 0;
  bottom: 0;
  overflow-y: scroll;
  scroll-snap-type: y mandatory;
  overscroll-behavior-y: contain;
`;

// Each slide must declare its own height against the live viewport (not the parent chain)
// so that scroll-snap increments remain correct as the browser toolbar shows/hides.
// height:100dvh is the dynamic viewport height — changes in real time on iOS Safari and
// Android Chrome as the URL bar appears/disappears. 100vh is the fallback for older browsers.
const VideoSlide = styled.div.attrs({ className: 'video-slide' })`
  height: calc(100vh - 60px);     /* fallback: older browsers */
  height: calc(100dvh - 60px);    /* dynamic: updates as URL bar shows/hides */
  width: 100%;
  flex-shrink: 0;
  scroll-snap-align: start;
  scroll-snap-stop: always;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #000;
  position: relative;
`;

const LoadingContainer = styled.div`
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px 20px;
  background-color: rgba(0, 0, 0, 0.7);
  border-radius: 20px;
  color: white;
  z-index: 100;
`;

const Spinner = styled(FaSpinner)`
  animation: spin 1s linear infinite;
  font-size: 18px;
  margin-right: 8px;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: white;
  text-align: center;
  padding: 20px;
  background-color: #121212;
`;

const RefreshButton = styled.button`
  background-color: #ff4757;
  color: white;
  border: none;
  border-radius: 50px;
  padding: 12px 24px;
  font-size: 16px;
  font-weight: bold;
  margin-top: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background-color: #ff6b81;
    transform: translateY(-2px);
    box-shadow: 0 7px 10px rgba(0, 0, 0, 0.2);
  }
  
  &:active {
    transform: translateY(1px);
    box-shadow: 0 2px 3px rgba(0, 0, 0, 0.1);
  }
  
  svg {
    margin-right: 8px;
    font-size: 18px;
  }
  
  &.refreshing {
    background-color: #2f3542;
    pointer-events: none;
    
    svg {
      animation: spin 1s linear infinite;
    }
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorIcon = styled(FaExclamationTriangle)`
  color: #ff4757;
  font-size: 48px;
  margin-bottom: 20px;
`;

const VirtualizedVideoFeed = forwardRef((props, ref) => {
  const { publicationId } = props;

  const containerRef = useRef(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [orderingMethod, setOrderingMethod] = useState('random');

  // IntersectionObserver — tracks which slide is active without touching scroll
  const observerRef = useRef(null);
  const slideRefs = useRef({});

  // ─── Design 048: sequenced, direction-aware warm plan ────────────────────────
  // Strictly ONE new download at a time: current first, then +d when current is buffer-healthy
  // (or 1500ms), then −d (skipped if its buffer survived) — every stage deadlined, any index
  // change resets the plan so fast swiping gives the whole pipe to where the user lands.
  const [warmMap, setWarmMap] = useState({ 0: 'auto' });
  const dirRef = useRef(1);                 // +1 forward (default), -1 backward
  const prevIndexRef = useRef(0);
  const planIndexRef = useRef(0);           // index the current plan belongs to
  const stageFiredRef = useRef({ s1: false, s2: false, s3: false });
  const stageTimersRef = useRef([]);
  const healthRef = useRef({});             // videoId -> latest warm-state report
  const allVideosRef = useRef([]);

  // Connection-awareness (may only SHRINK the plan; Safari lacks the API => full plan)
  const maxWarmStage = () => {
    const c = navigator.connection;
    if (!c) return 2;
    if (c.saveData) return 0;
    const t = c.effectiveType || '';
    if (t.includes('2g')) return 0;
    if (t === '3g') return 1;
    return 2;
  };

  // P2.1: a deadline must NOT stack a neighbor download onto a link the current video is still
  // fighting for (that re-creates the contention this design removes, exactly on slow links).
  // The deadline defers while the reference slide is actively downloading-but-unhealthy (fresh
  // report, frames pending), re-arming up to REARM_MAX times before firing anyway (liveness).
  const REARM_MAX = 6; // ~6 x 1.5s ≈ 9s of protected loading before the backstop wins
  const rearmRef = useRef({ s1: 0, s2: 0 });

  const _activelyLoading = (videoId) => {
    const rep = healthRef.current[videoId];
    return !!(rep && !rep.healthy && rep.at && (performance.now() - rep.at) < 2500);
  };

  const fireStage1 = useCallback((planIdx, fromDeadline = false) => {
    if (planIndexRef.current !== planIdx || stageFiredRef.current.s1) return;
    if (fromDeadline && rearmRef.current.s1 < REARM_MAX
        && _activelyLoading(allVideosRef.current[planIdx]?.id)) {
      rearmRef.current.s1 += 1;
      stageTimersRef.current.push(setTimeout(() => fireStage1(planIdx, true), 1500));
      return;
    }
    stageFiredRef.current.s1 = true;
    if (maxWarmStage() < 1) return;
    const ahead = planIdx + dirRef.current;
    if (ahead < 0 || ahead >= allVideosRef.current.length) return;
    setWarmMap(m => (m[ahead] === 'auto' ? m : { ...m, [ahead]: 'auto' }));
  }, []);

  const fireStage3 = useCallback((planIdx) => {
    // P2: metadata-only warm of +2d — a fast double-swipe then skips the moov roundtrip.
    if (planIndexRef.current !== planIdx || stageFiredRef.current.s3) return;
    stageFiredRef.current.s3 = true;
    if (maxWarmStage() < 2) return;
    const far = planIdx + dirRef.current * 2;
    if (far < 0 || far >= allVideosRef.current.length) return;
    setWarmMap(m => (m[far] ? m : { ...m, [far]: 'metadata' }));
  }, []);

  const fireStage2 = useCallback((planIdx, fromDeadline = false) => {
    if (planIndexRef.current !== planIdx || stageFiredRef.current.s2) return;
    if (fromDeadline && rearmRef.current.s2 < REARM_MAX) {
      const curId = allVideosRef.current[planIdx]?.id;
      const aheadId = allVideosRef.current[planIdx + dirRef.current]?.id;
      if (_activelyLoading(curId) || _activelyLoading(aheadId)) {
        rearmRef.current.s2 += 1;
        stageTimersRef.current.push(setTimeout(() => fireStage2(planIdx, true), 1500));
        return;
      }
    }
    stageFiredRef.current.s2 = true;
    if (maxWarmStage() >= 2) {
      const behind = planIdx - dirRef.current;
      if (behind >= 0 && behind < allVideosRef.current.length) {
        // Buffer-checked: if the behind slide's buffer survived, warming is a no-op (costs nothing).
        const rep = healthRef.current[allVideosRef.current[behind]?.id];
        if (!(rep && rep.bufferedAhead >= 3)) {
          setWarmMap(m => (m[behind] === 'auto' ? m : { ...m, [behind]: 'auto' }));
        }
      }
    }
    // Stage 2 settled -> stage 3 may proceed (metadata fetch is tiny; 4000ms timer is the backstop).
    fireStage3(planIdx);
  }, [fireStage3]);

  // Health reports from players advance the plan early (deadlines below are the backstop).
  const handleWarmState = useCallback((videoId, report) => {
    healthRef.current[videoId] = { ...report, at: performance.now() };
    if (!report.healthy) return;
    const idx = planIndexRef.current;
    const d = dirRef.current;
    const vids = allVideosRef.current;
    if (vids[idx]?.id === videoId) fireStage1(idx);
    else if (vids[idx + d]?.id === videoId) fireStage2(idx);
  }, [fireStage1, fireStage2]);

  // (Re)start the plan on every settle of currentIndex.
  useEffect(() => {
    const d = currentIndex >= prevIndexRef.current ? 1 : -1;
    dirRef.current = d;
    prevIndexRef.current = currentIndex;
    planIndexRef.current = currentIndex;
    stageFiredRef.current = { s1: false, s2: false, s3: false };
    rearmRef.current = { s1: 0, s2: 0 };
    stageTimersRef.current.forEach(clearTimeout);
    stageTimersRef.current = [
      setTimeout(() => fireStage1(currentIndex, true), 1500),
      setTimeout(() => fireStage2(currentIndex, true), 2500),
      setTimeout(() => fireStage3(currentIndex), 4000),
    ];
    // Stage 0: the active slide warms immediately, unconditionally. The map is REBUILT per plan
    // (review MEDIUM): stale 'auto' entries from earlier passes would let remounting slides start
    // downloads in parallel with the landing slide, bypassing the sequencing. Dropping a slide to
    // 'none' never removes an attached src, so no buffer is destroyed.
    setWarmMap({ [currentIndex]: 'auto' });
    return () => {
      stageTimersRef.current.forEach(clearTimeout);
    };
  }, [currentIndex, fireStage1, fireStage2, fireStage3]);
  // ─────────────────────────────────────────────────────────────────────────────

  // Keep load-more dependencies in a ref so the observer callback never goes stale
  const loadMoreStateRef = useRef({ allVideosLength: 0, hasMore: false, loading: false, loadMore: () => {} });

  usePoints();

  const fetchFn = useCallback(
    (cursor) => fetchVideos(cursor, 10, publicationId, orderingMethod),
    [publicationId, orderingMethod]
  );

  const {
    data: rawVideos,
    loading,
    error,
    hasMore,
    ref: infiniteScrollRef,
    refresh: refreshVideos,
    loadMore,
  } = useInfiniteScroll(fetchFn, { initialLoading: true });

  const maxVideos = 100;
  const allVideos = rawVideos.slice(0, maxVideos);

  // Keep load-more state ref current so the observer callback can read fresh values
  useEffect(() => {
    loadMoreStateRef.current = { allVideosLength: allVideos.length, hasMore, loading, loadMore };
  }, [allVideos.length, hasMore, loading, loadMore]);

  // Design 048: keep the warm plan's video list current (read by stage callbacks via ref)
  useEffect(() => {
    allVideosRef.current = allVideos;
  }, [allVideos]);

  // Design 048 P2.1: prefetch poster images for slides in/near the render window so the first
  // frame image is already in the HTTP cache when the user lands (posters were taking seconds
  // because their fetch queued behind video downloads). Tiny JPEGs; each fetched once.
  const posterPrefetchedRef = useRef(new Set());
  useEffect(() => {
    const start = Math.max(0, currentIndex - 2);
    const end = Math.min(allVideos.length - 1, currentIndex + 3);
    for (let i = start; i <= end; i++) {
      const t = allVideos[i]?.thumbnail_url;
      if (t && !posterPrefetchedRef.current.has(t)) {
        posterPrefetchedRef.current.add(t);
        const img = new Image();
        img.src = t;
      }
    }
  }, [currentIndex, allVideos]);

  // Set up IntersectionObserver once on mount — fires when a slide is ≥50% visible
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            const idx = Number(entry.target.dataset.index);
            if (!isNaN(idx)) {
              setCurrentIndex(prev => (prev !== idx ? idx : prev));
              const { allVideosLength, hasMore: hm, loading: ld, loadMore: lm } = loadMoreStateRef.current;
              if (idx >= allVideosLength - 3 && hm && !ld) lm();
            }
          }
        });
      },
      { threshold: 0.5 }
    );

    // Observe any slides already registered (handles React strict-mode double-mount)
    Object.values(slideRefs.current).forEach(el => {
      if (el) observerRef.current.observe(el);
    });

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Register / unregister each VideoSlide with the observer via ref callback
  const registerSlide = useCallback((el, index) => {
    if (!el) {
      const existing = slideRefs.current[index];
      if (existing && observerRef.current) observerRef.current.unobserve(existing);
      delete slideRefs.current[index];
    } else {
      slideRefs.current[index] = el;
      if (observerRef.current) observerRef.current.observe(el);
    }
  }, []);

  // Advance to next video — CSS snap + scrollIntoView; IntersectionObserver confirms currentIndex
  const goToNext = useCallback(() => {
    const nextIdx = currentIndex + 1;
    if (nextIdx >= allVideos.length) return;
    const nextSlide = slideRefs.current[nextIdx];
    if (nextSlide) {
      nextSlide.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setCurrentIndex(nextIdx);
  }, [currentIndex, allVideos.length]);

  // ─── Design 048: desktop wheel damper — ONE gesture = ONE video ─────────────
  // Free-spinning/high-res mouse wheels fling past several full-height slides per flick
  // (scroll-snap-stop:always is honored inconsistently for high-velocity wheel input) —
  // measured live as 125 sub-0.7s video-to-video transitions in one session, a huge
  // contention amplifier. Wheel input is intercepted and quantised: accumulate the gesture,
  // step exactly one slide, then lock until the fling's residual deltas die down.
  // Touch is unaffected (no wheel events); trackpads get the same 1-per-gesture behavior.
  const wheelStateRef = useRef({ acc: 0, lockedUntil: 0, lastEventAt: 0 });
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const step = (dir) => {
      const idx = planIndexRef.current + dir;
      if (idx < 0 || idx >= allVideosRef.current.length) return;
      const slide = slideRefs.current[idx];
      if (slide) slide.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setCurrentIndex(idx);
    };
    const onWheel = (e) => {
      e.preventDefault();
      const s = wheelStateRef.current;
      const now = performance.now();
      // A gesture's residual momentum keeps emitting deltas — swallow them while locked,
      // and extend the lock so a long fling still means one step.
      if (now < s.lockedUntil) { s.lockedUntil = now + 250; return; }
      if (now - s.lastEventAt > 200) s.acc = 0;   // new gesture
      s.lastEventAt = now;
      s.acc += e.deltaY;
      if (Math.abs(s.acc) >= 40) {
        const dir = s.acc > 0 ? 1 : -1;
        s.acc = 0;
        s.lockedUntil = now + 450;
        step(dir);
      }
    };
    const onKey = (e) => {
      if (e.key === 'ArrowDown' || e.key === 'PageDown') { e.preventDefault(); step(1); }
      else if (e.key === 'ArrowUp' || e.key === 'PageUp') { e.preventDefault(); step(-1); }
    };
    container.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('keydown', onKey);
    return () => {
      container.removeEventListener('wheel', onWheel);
      window.removeEventListener('keydown', onKey);
    };
  }, []); // refs only — stable for the feed's lifetime

  const handleRefresh = useCallback(() => {
    setIsRefreshing(true);
    setCurrentIndex(0);
    setWarmMap({ 0: 'auto' });      // design 048 review: indices remap to different videos
    healthRef.current = {};
    if (containerRef.current) containerRef.current.scrollTop = 0;
    return refreshVideos()
      .then(result => { setIsRefreshing(false); return result; })
      .catch(err => { console.error('Error in refresh:', err); setIsRefreshing(false); throw err; });
  }, [refreshVideos]);

  const changeOrdering = useCallback((method) => {
    setOrderingMethod(method);
    setCurrentIndex(0);
    setWarmMap({ 0: 'auto' });      // design 048 review: indices remap to different videos
    healthRef.current = {};
  }, []);

  // Pause and clean up all videos on unmount
  useEffect(() => {
    const container = containerRef.current;
    return () => {
      if (container) {
        container.querySelectorAll('video').forEach(v => { v.pause(); v.muted = true; v.currentTime = 0; });
      }
    };
  }, []);

  // Clear resume positions for videos more than ±3 slides from current
  useEffect(() => {
    if (!allVideos || allVideos.length === 0) return;
    const start = Math.max(0, currentIndex - 3);
    const end = Math.min(allVideos.length, currentIndex + 4);
    pruneResumeWindow(allVideos.slice(start, end).map(v => v.id));
  }, [currentIndex, allVideos]);

  useImperativeHandle(ref, () => ({
    refresh: handleRefresh,
    changeOrdering,
    goToNext,
    getCurrentIndex: () => currentIndex,
    getTotalVideos: () => allVideos.length,
  }));

  if (error) {
    return (
      <EmptyState>
        <ErrorIcon />
        <h2>Error Loading Videos</h2>
        <p>Please check your connection and try again.</p>
        <RefreshButton onClick={handleRefresh} className={isRefreshing ? 'refreshing' : ''} disabled={isRefreshing}>
          <FaSync /> {isRefreshing ? 'Refreshing...' : 'Refresh Videos'}
        </RefreshButton>
      </EmptyState>
    );
  }

  if (loading && allVideos.length === 0) {
    return (
      <EmptyState>
        <h2>Loading vusub.ai</h2>
        <Spinner />
        <p style={{ marginTop: '20px' }}>Fetching videos...</p>
      </EmptyState>
    );
  }

  if (!loading && allVideos.length === 0) {
    return (
      <EmptyState>
        <h2>No videos found</h2>
        <p>Check back later for new content!</p>
        <RefreshButton onClick={handleRefresh} className={isRefreshing ? 'refreshing' : ''} disabled={isRefreshing}>
          <FaSync /> {isRefreshing ? 'Refreshing...' : 'Refresh Videos'}
        </RefreshButton>
      </EmptyState>
    );
  }

  const windowStart = Math.max(0, currentIndex - 2);
  const windowEnd = Math.min(allVideos.length - 1, currentIndex + 2);

  return (
    <FeedContainer ref={containerRef}>
      {allVideos.map((video, index) => {
        const inWindow = index >= windowStart && index <= windowEnd;
        const isActive = index === currentIndex;

        return (
          <VideoSlide
            key={video.id}
            data-index={index}
            ref={(el) => registerSlide(el, index)}
          >
            {inWindow && (
              <VideoPlayer
                key={video.id}
                video={video}
                autoPlay={true}
                inView={isActive}
                warmLevel={warmMap[index] || 'none'}
                onWarmState={handleWarmState}
                onVideoComplete={goToNext}
                publicationId={publicationId}
              />
            )}
          </VideoSlide>
        );
      })}

      {loading && allVideos.length > 0 && (
        <LoadingContainer>
          <Spinner />
          Loading more videos...
        </LoadingContainer>
      )}

      {/* Infinite scroll trigger — positioned after all slides */}
      <div ref={infiniteScrollRef} style={{ height: '1px' }} />
    </FeedContainer>
  );
});

export default VirtualizedVideoFeed;
